/**
 * Wallpaper aurora — latar bergerak di belakang seluruh aplikasi.
 *
 * Dipakai di `Layout.tsx` sebagai anak pertama, jadi posisinya `fixed` dan tidak
 * mengambil ruang di flow. Konten aplikasi tetap duduk di atasnya.
 *
 * Kenapa canvas 2D dan bukan WebGL/three.js:
 *
 * 1. Yang dibutuhkan cuma "kabut warna bergerak pelan". WebGL untuk ini menambah
 *    ratusan KB dan shader yang harus ditulis manual, sementara canvas 2D bisa
 *    menggabungkan gradient radial dengan
 *    `globalCompositeOperation = 'lighter'` untuk menghasilkan pantulan cahaya
 *    yang sama.
 * 2. Aplikasi ini tidak punya canvas lain dan tidak butuh interaksi 3D, jadi
 *    berdependensi WebGL berarti library berat untuk satu efek dekoratif.
 * 3. `prefers-reduced-motion` harus dihormati. Dengan canvas, motionless berarti
 *    benar-benar berhenti (satu frame statis); dengan animasi CSS, override sering
 *    tetap jalan di GPU walau animasi dimatikan.
 *
 * Batasan yang disengaja:
 * - Loop dihentikan saat tab tidak terlihat (`visibilitychange`). Aurora di
 *   background tab adalah pemborosan baterai yang tidak terlihat hasilnya.
 * - Resolusi dibatasi (lihat DPR_CAP) supaya canvas 4K tidak memakai 8 juta
 *   piksel per frame.
 * - Titik-titik cahaya memakai `requestAnimationFrame` dengan delta waktu, bukan
 *   `setInterval`, jadi gerak tetap halus di monitor 120Hz dan tidak melaju di
 *   layar 30Hz.
 */

import { useEffect, useRef } from 'react'

/**
 * Batas device pixel ratio. Di atas inibenefit visual hampir tak terlihat
 * (aurora sudah kabut) sementara biaya fill rate naik kuadratik.
 */
const DPR_CAP = 1.5

/** Jumlah gumpalan cahaya. lebih banyak = halus tapi lebih mahal. */
const BLOB_COUNT = 7

/** Kecepatan dasar, dalam radian per milidetik. */
const DRIFT = 0.00012

/**
 * Palet per tema. Nilai taken dari token brand aplikasi (jade + amber) supaya
 * wallpaper terasa bagian dari produk, bukan hiasan generik.
 *
 * Mode terang sengaja memakai intensitas lebih rendah dan alpha lebih kecil:
 * teks hitam di atas aurora terang jauh lebih sulit dibaca daripada teks putih
 * di atas aurora gelap.
 */
const PALETTE = {
  light: {
    // [hue, saturation, lightness] — aurora terang butuh warna soft.
    colors: [
      [160, 70, 62],
      [172, 75, 66],
      [42, 85, 66],
      [150, 60, 70],
      [188, 70, 68],
      [35, 80, 70],
      [165, 55, 74],
    ],
    alpha: 0.34,
  },
  dark: {
    colors: [
      [160, 80, 45],
      [172, 85, 50],
      [42, 90, 52],
      [150, 75, 42],
      [188, 80, 48],
      [35, 85, 55],
      [165, 70, 50],
    ],
    alpha: 0.28,
  },
} as const

type Rgb = readonly [number, number, number]

function readTheme(): 'light' | 'dark' {
  const attr = document.documentElement.dataset.theme
  if (attr === 'light' || attr === 'dark') return attr
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

export default function AuroraBackdrop() {
  const ref = useRef<HTMLCanvasElement | null>(null)

  useEffect(() => {
    const canvas = ref.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    // Alias non-null untuk dipakai di dalam closure. TypeScript kehilangan
    // narrowing `canvas`/`ctx` di dalam fungsi yang dibuat setelah pemeriksaan,
    // padahal keduanya dijamin ada sampai effect dibersihkan - canvas tidak pernah
    // diganti, dan context 2D tidak pernah dibuang.
    const el: HTMLCanvasElement = canvas
    const c2d: CanvasRenderingContext2D = ctx

    // `prefers-reduced-motion` dibaca ulang tiap clear/restart, bukan hanya saat
    // mount: pengguna bisa mengubah setelan sistem tanpa me-reload halaman.
    const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)')

    let theme: 'light' | 'dark' = readTheme()
    let width = 0
    let height = 0
    let raf = 0
    let running = true

    type Blob = { x: number; y: number; r: number; hue: number; drift: number; phase: number }
    let blobs: Blob[] = []

    function seedBlobs() {
      // Posisi awal tersebar deterministik (bukan Math.random) supaya dua muat
      // halaman menghasilkan Susunan sama - kalau random, membandingkan dua
      // screenshot akan selalu berbeda dan sulit menilai perubahan efek.
      blobs = Array.from({ length: BLOB_COUNT }, (_, i) => {
        const a = (i / BLOB_COUNT) * Math.PI * 2
        return {
          x: width * (0.5 + 0.42 * Math.cos(a)),
          y: height * (0.5 + 0.42 * Math.sin(a)),
          r: Math.max(width, height) * (0.32 + 0.12 * ((i % 3) / 2)),
          hue: i,
          drift: 1 + (i % 4) * 0.35,
          phase: a,
        }
      })
    }

    function resize() {
      const dpr = Math.min(window.devicePixelRatio || 1, DPR_CAP)
      width = el.clientWidth
      height = el.clientHeight
      if (width === 0 || height === 0) return
      el.width = Math.round(width * dpr)
      el.height = Math.round(height * dpr)
      c2d.setTransform(dpr, 0, 0, dpr, 0, 0)
      seedBlobs()
    }

    function draw(t: number) {
      const { colors, alpha } = PALETTE[theme]

      // Canvas dibersihkan tiap frame. Mode gelap tidak boleh menumpuk jejak: transparency
      // di atas kanvas gelap akan menaikkan brightness seiring waktu sampai layar
      // jadi putih.
      c2d.clearRect(0, 0, width, height)

      // 'lighter' = penjumlahan cahaya, bukan alpha blending biasa. Inilah yang
      // membuat irisan antar gumpalan terlihat terang seperti aurora, bukan
      // seperti bercak cat yang saling menutupi.
      c2d.globalCompositeOperation = 'lighter'

      for (const blob of blobs) {
        const [h, s, l] = colors[blob.hue % colors.length] as Rgb
        // Radius berdenyut pelan, tidak mengikuti drift penuh, supaya gumpalan
        // terasa "bernyawa" dan bukan berputar seperti rotor.
        const breathe = 1 + 0.08 * Math.sin(t * 0.0004 + blob.phase)
        const r = blob.r * breathe
        const g = c2d.createRadialGradient(blob.x, blob.y, 0, blob.x, blob.y, r)
        const a = alpha.toFixed(3)
        g.addColorStop(0, `hsla(${h}, ${s}%, ${l}%, ${a})`)
        g.addColorStop(0.55, `hsla(${h}, ${s}%, ${l}%, ${(alpha * 0.42).toFixed(3)})`)
        g.addColorStop(1, `hsla(${h}, ${s}%, ${l}%, 0)`)
        c2d.fillStyle = g
        c2d.beginPath()
        c2d.arc(blob.x, blob.y, r, 0, Math.PI * 2)
        c2d.fill()
      }

      c2d.globalCompositeOperation = 'source-over'
    }

    function frame(t: number) {
      if (!running) return
      for (const blob of blobs) {
        // Orbit ringan: x dan y bergerak pada frekuensi berbeda supaya jalurnya
        // elips, bukan lingkaran.
        const a = t * DRIFT * blob.drift + blob.phase
        blob.x = width * (0.5 + 0.42 * Math.cos(a))
        blob.y = height * (0.5 + 0.34 * Math.sin(a * 1.37))
      }
      draw(t)
      raf = window.requestAnimationFrame(frame)
    }

    function renderStatic() {
      for (const blob of blobs) {
        const a = (blob.phase + Math.PI / 3) % (Math.PI * 2)
        blob.x = width * (0.5 + 0.42 * Math.cos(a))
        blob.y = height * (0.5 + 0.34 * Math.sin(a))
      }
      draw(0)
    }

    function start() {
      window.cancelAnimationFrame(raf)
      if (motionQuery.matches) {
        renderStatic()
        return
      }
      raf = window.requestAnimationFrame(frame)
    }

    // Tema berubah -> palette berubah. Effect-nya ditambahkan terpisah supaya
    // ganti tema tidak ikut memicu `resize()` (yang akan membuat seed ulang dan
    // terasa seperti kedipan).
    const themeObserver = new MutationObserver(() => {
      const next = readTheme()
      if (next === theme) return
      theme = next
      start()
    })
    themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] })

    const onMotionChange = () => start()
    motionQuery.addEventListener('change', onMotionChange)

    const onVisibility = () => {
      const hidden = document.hidden
      if (hidden) {
        running = false
        window.cancelAnimationFrame(raf)
      } else if (!running) {
        running = true
        start()
      }
    }
    document.addEventListener('visibilitychange', onVisibility)

    const ro = new ResizeObserver(() => {
      resize()
      start()
    })
    ro.observe(el)

    resize()
    // Kalau tab sedang tersembunyi saat mount (mis. halaman di prerender), jangan
    // mulai loop yang tidak akan pernah terlihat.
    running = !document.hidden
    start()

    return () => {
      running = false
      window.cancelAnimationFrame(raf)
      ro.disconnect()
      themeObserver.disconnect()
      motionQuery.removeEventListener('change', onMotionChange)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [])

  return (
    <canvas
      ref={ref}
      aria-hidden="true"
      className="aurora-backdrop"
      // `pointer-events-none` wajib: tanpa itu canvas menutup klik/tap seluruh
      // aplikasi, karena ia tetap elemen full-screen di atas flow.
    />
  )
}
